import * as storybookStubs from '@scale/frontend-lib/storybookHelpers/storybookStubs';
import _ from 'lodash';
import cookies from 'browser-cookies';
import qs from 'query-string';
import SAlert from 'react-s-alert';
import { Assign } from 'utility-types';

import authFetch from './auth-fetch';
import { tryParseErrorResponse, displayErrorMessage } from './errorMessages';
import { getPageLoadSingleton, getBranchSingleton } from './globalSingletons';
import HTTPError from './httpError';
import { captureClientSideError } from './errors';
import { getIpAddress } from '@scale/shared/getIpAddress';
import FormData from 'form-data';
import { v4 as uuid } from 'uuid';
import { EventSourceMessage } from '@microsoft/fetch-event-source';
import redirect from './isomorphic-redirect';
import logger from '@scale/frontend-lib/logging';
import fetch from 'isomorphic-fetch';

export { HTTPError };

interface IDefaultApiFetchErrorHandlerOptions {
  handleHttp?: boolean;
  message?: string;
}

export function defaultApiFetchErrorHandler(
  error: HTTPError,
  { handleHttp, message }: IDefaultApiFetchErrorHandlerOptions = {},
) {
  if ((process.browser && !error.response) || handleHttp) {
    handleApiFetchError(error, message);
  }
}

export function handleApiFetchError(error: HTTPError, message?: string) {
  captureClientSideError(error);
  SAlert.error(displayErrorMessage(error, undefined, message), {
    timeout: 'none',
  });
}

function shouldFormatJSON(res: Response) {
  return (res.headers.get('content-type') || '').startsWith('application/json');
}

export type ApiFetchOptions<B = Record<string, any>, Q = Record<string, any>> = Assign<
  RequestInit,
  {
    body?: RequestInit['body'] | B;
    query?: Q;
    returnRawResponse?: boolean;
    parseAndHandle?: boolean;
    loginUrl?: string;
    noAuth?: boolean;
    headers?: Record<string, string>;
    redirectOnUnauthorized?: boolean;
    maxRetries?: number;
    binary?: boolean;
    signal?: AbortController['signal'];
    allowedStatusCodes?: number[];
    idempotencyKey?: string;
    passError?: boolean;
    fetchEventSourceOptions?: {
      signal?: AbortController['signal'];
      onopen?: (response: Response) => Promise<void>;
      onmessage?: (ev: EventSourceMessage) => void;
      onclose?: () => void;
      onerror?: (err: any) => number | null | undefined | void;
      openWhenHidden?: boolean;
    };
  }
>;

/**
 * @param context - Next.js context object, or null if not server-side. If null, you must make sure window is defined!
 * @param url - API endpoint to fetch from server.
 * @param options - Might contain additional headers.
 */
// eslint-disable-next-line complexity
export default async function apiFetch<R = any, B = any>(
  context: Record<string, any> | null,
  url: string,
  options: ApiFetchOptions<B> = {},
): Promise<R> {
  if (process.env.STORYBOOK_OFFLINE_MODE) {
    if (process.env.OUTLIER_STORYBOOK) {
      // for storybook, make the network request directly
      // stub the request in the story's parameters.
      // see ProjectOnboarding.stories.tsx for an example
      // eslint-disable-next-line no-console
      console.log(`storybook is fetching url ${url}`);
      try {
        return (await fetch(url)).json();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(`storybook errored fetching url ${url} has error ${err}`);
        throw err;
      }
    }
    // other storybooks use the stubs defined here, but they are defined globally, not per story.
    return storybookStubs.apiFetch(context, url);
  }
  const returnRawResponse = !!options.returnRawResponse;
  delete options.returnRawResponse;
  const fetchEventSourceOptions = options.fetchEventSourceOptions;

  const currentLocation: string = context ? context.pathname : window.location.href;

  if (currentLocation.match(/\/corp($|\/)/g)) {
    options.loginUrl = '/corp/login';
  }

  options.headers = options.headers || {};
  if (context && context.req) {
    if (context.req.headers['cf-ipcountry']) {
      options.headers['ssr-Front-IpCountry'] = context.req.headers['cf-ipcountry'];
    }
    options.headers['Client-Ip'] = getIpAddress(context.req);
    options.headers.Cookie = context.req.headers.cookie;
    options.headers['X-CSRF-Token'] = context.req.cookies._csrf ?? 'blank';
  } else {
    options.headers['X-CSRF-Token'] = cookies.get('_csrf') ?? 'blank';
  }

  if (options.body && !(options.body instanceof FormData)) {
    if (!(options.headers as { [key: string]: string })['Content-Type']) {
      options.body = JSON.stringify(options.body);
      options.headers['Content-Type'] = 'application/json';
    }
  }
  if (options.idempotencyKey)
    options.headers['idempotency-key'] = options.idempotencyKey.concat(
      `-${uuid().substring(0, 6)}`,
    );
  let pathname = url;

  // On frontend, always include pageLoadId in API requests (for debugging)
  if (process.browser) {
    _.set(options, 'query.pageLoadId', getPageLoadSingleton());
  } else if (
    !process.env.API_URL!.includes('.com') &&
    process.env.API_URL!.startsWith('https://')
  ) {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const https = require('https');
    // @ts-ignore node-only option
    options.agent = new https.Agent({
      rejectUnauthorized: false,
    });
  }

  if (options.query && !_.isEmpty(options.query)) {
    // NOTE: many calls to apiFetch inline a query into the pathname instead of using the query option,
    // so we have to check whether any params are already present
    let sep = /\?.+=.+/.test(url) ? '&' : '?';
    // somehow a stray '?' can sometimes be at the end...
    if (pathname.endsWith('?')) sep = '';
    pathname += sep + qs.stringify(options.query);
  }

  if (pathname.startsWith('/')) {
    pathname = pathname.slice(1);
  }

  let path = `${process.browser ? '' : process.env.API_URL}/${pathname}`;

  if (path.startsWith('/http')) {
    path = path.substring(1);
  }

  try {
    if (process.browser) {
      const loadedBranch = getBranchSingleton();
      if (loadedBranch) {
        options.headers['branch-onload'] = loadedBranch;
      }
    } else {
      const loadedBranch = context?.req?.cookies?.branch;
      if (loadedBranch) {
        options.headers['branch-onload'] = loadedBranch;
      }
    }
  } catch (e) {
    logger.error(`Error setting branch-onload header ${e}`);
  }

  const res = (await authFetch(context, path, options)) as Response;

  if (returnRawResponse || !!fetchEventSourceOptions) {
    return res as any; // HACK `as any` helps avoid needing to narrow union type for all API calls
  }

  if (!res.ok && !options.allowedStatusCodes?.includes(res.status)) {
    if (res.status === 401 && currentLocation.includes('outlier')) {
      redirect(context, '/expert/login', {
        includeCurrentURL: true,
        clear: true,
      });
    }

    const errorMessage = options.passError
      ? (await tryParseErrorResponse(res))?.message || res.statusText
      : res.statusText;

    throw new HTTPError(errorMessage, await tryParseErrorResponse(res));
  }

  if (options.binary) {
    return res.blob() as Promise<any>;
  }

  return shouldFormatJSON(res) ? res.json() : (res.text() as R);
}
